import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { RouterConstants } from '../constants/router.constants';
import { ServiceLocalStorage } from './local-storage.service';
import { ApiConstants } from '../constants/api-path.constants';
import { AppConstants } from '../constants/appconstants';
import { environment } from 'src/environments/environment';
import { SessionExpiryDialogComponent } from '../../layouts/session-expiry-dialog/session-expiry-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  static readonly SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';
  static readonly SESSION_EXPIRED_FLAG = 'sessionExpired';
  static readonly EXPIRY_WARNING_MS = 5 * 60 * 1000;
  static readonly REFRESH_INTERVAL_MS = 14 * 60 * 1000;

  AUTH_TOKEN = 'auth-token';
  authToken: any = undefined;

  private readonly jwtHelper = new JwtHelperService();
  private readonly httpWithoutInterceptor: HttpClient;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private refreshInProgress: Observable<boolean> | null = null;
  private expiryDialogOpen = false;
  private expiryDialogRef: any = null;
  private loggingOut = false;
  private continueSessionRefresh = false;

  constructor(
    private router: Router,
    private readonly serviceLocalStorage: ServiceLocalStorage,
    private readonly httpBackend: HttpBackend,
    private readonly dialog: MatDialog
  ) {
    this.httpWithoutInterceptor = new HttpClient(httpBackend);
  }

  checkUserlogin(): boolean {
    const jwt: any = this.serviceLocalStorage.getAuthToken();
    if (jwt === null || jwt === undefined) {
      return false;
    }

    const expiryTimeMs = this.getStoredSessionExpiryTimeMs();
    if (expiryTimeMs !== null) {
      return Date.now() < expiryTimeMs;
    }

    return !this.jwtHelper.isTokenExpired(jwt);
  }

  shouldUpdateExpiryOnRenewJwt(): boolean {
    return this.continueSessionRefresh;
  }

  applySessionExpiryFromHeaders(headers: { get(name: string): string | null }): boolean {
    const expiresAt = headers.get(AppConstants.Expires_At);
    if (!expiresAt || isNaN(Number(expiresAt))) {
      return false;
    }

    localStorage.setItem(AppConstants.SESSION_EXPIRES_AT_KEY, expiresAt);
    if (this.serviceLocalStorage.hasAuthToken()) {
      this.startSessionMonitoring();
    }
    return true;
  }

  logout(): void {
    this.stopSessionMonitoring();
    this.clearAuthStorage(false);
    this.router.navigate(
      ['/' + RouterConstants.auth + '/' + RouterConstants.client],
      { queryParams: { signedOut: true } }
    );
  }

  logoutDueToExpiry(showMessage: boolean = true): void {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.stopSessionMonitoring();
    this.closeExpiryDialogIfOpen();

    this.clearAuthStorage(showMessage);

    this.router.navigate(
      ['/' + RouterConstants.auth + '/' + RouterConstants.client],
      {
        queryParams: showMessage
          ? { sessionExpired: true }
          : { signedOut: true }
      }
    ).finally(() => {
      this.loggingOut = false;
    });
  }

  private clearAuthStorage(showExpiryMessage: boolean = false): void {
    sessionStorage.clear();
    this.serviceLocalStorage.deleteAll();

    if (showExpiryMessage) {
      sessionStorage.setItem(AuthenticationService.SESSION_EXPIRED_FLAG, 'true');
    }
  }

  private closeExpiryDialogIfOpen(): void {
    if (this.expiryDialogRef) {
      this.expiryDialogRef.close('auto');
      this.expiryDialogRef = null;
      this.expiryDialogOpen = false;
    }
  }

  startSessionMonitoring(): void {
    this.stopSessionMonitoring();

    if (!this.serviceLocalStorage.getAuthToken()) {
      return;
    }

    const expiryTimeMs = this.getStoredSessionExpiryTimeMs();
    if (expiryTimeMs === null) {
      return;
    }

    const msUntilExpiry = expiryTimeMs - Date.now();
    if (msUntilExpiry <= 0) {
      this.logoutDueToExpiry();
      return;
    }

    const msUntilWarning = msUntilExpiry - AuthenticationService.EXPIRY_WARNING_MS;
    if (msUntilWarning > 0) {
      this.warningTimer = setTimeout(
        () => this.showExpiryWarning(expiryTimeMs),
        msUntilWarning
      );
    }

    this.logoutTimer = setTimeout(() => this.logoutDueToExpiry(), msUntilExpiry);

    this.refreshIntervalId = setInterval(() => {
      if (this.serviceLocalStorage.hasAuthToken()) {
        this.tryRefreshToken(false).subscribe();
      }
    }, AuthenticationService.REFRESH_INTERVAL_MS);
  }

  stopSessionMonitoring(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }

  canRefreshToken(): boolean {
    const refreshToken = this.serviceLocalStorage.getRefToken();
    const userId = this.serviceLocalStorage.getUserId();
    const clientCode = localStorage.getItem('clientCode');
    return !!(refreshToken && userId && clientCode);
  }

  tryRefreshToken(updateExpiresAt: boolean = false): Observable<boolean> {
    if (!this.canRefreshToken()) {
      return of(false);
    }

    if (this.refreshInProgress && !updateExpiresAt) {
      return this.refreshInProgress;
    }

    this.refreshInProgress = this.doRefreshToken(updateExpiresAt).pipe(
      finalize(() => {
        this.refreshInProgress = null;
        if (updateExpiresAt) {
          this.continueSessionRefresh = false;
        }
      }),
      shareReplay(1)
    );

    return this.refreshInProgress;
  }

  handleUnauthorizedFromApi(errorMessage?: string): void {
    if (this.isLoginFlowError(errorMessage)) {
      return;
    }

    if (this.canRefreshToken()) {
      this.tryRefreshToken(false).subscribe((success) => {
        if (!success) {
          this.logoutDueToExpiry();
        }
      });
      return;
    }

    this.logoutDueToExpiry();
  }

  shouldLogoutOnUnauthorized(status: number, message?: string): boolean {
    if (status === 400 && message === 'Malformed Token') {
      return true;
    }

    if (status !== 401) {
      return false;
    }

    if (this.isLoginFlowError(message)) {
      return false;
    }

    return (
      message === 'Session Expired' ||
      message === 'Expired Token' ||
      message === 'Malformed Token' ||
      (!message && this.serviceLocalStorage.hasAuthToken())
    );
  }

  consumeSessionExpiredFlag(): boolean {
    const expired = sessionStorage.getItem(AuthenticationService.SESSION_EXPIRED_FLAG) === 'true';
    if (expired) {
      sessionStorage.removeItem(AuthenticationService.SESSION_EXPIRED_FLAG);
    }
    return expired;
  }

  private doRefreshToken(updateExpiresAt: boolean = false): Observable<boolean> {
    const refreshToken = this.serviceLocalStorage.getRefToken();
    const userId = this.serviceLocalStorage.getUserId();
    const clientCode = localStorage.getItem('clientCode');
    const url = environment.baseUrl + ApiConstants.renewJWT;
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .append('Client_Code', clientCode as string);
    if (updateExpiresAt) {
      headers = headers.append(AppConstants.SESSION_CONTINUE_REFRESH, 'true');
    }
    const body = { refreshToken, userId };

    return this.httpWithoutInterceptor
      .post(url, JSON.stringify(body), { headers, observe: 'response' })
      .pipe(
        map((response: any) => {
          if (response.status !== 200) {
            return false;
          }

          const bearerToken = response.body?.accessToken;
          if (
            bearerToken &&
            bearerToken.length > 0 &&
            bearerToken.slice(0, AppConstants.BEARER_STRING.length) === AppConstants.BEARER_STRING
          ) {
            this.serviceLocalStorage.setAuthToken(
              bearerToken.slice(AppConstants.BEARER_STRING.length)
            );
            const newRefreshToken = response.headers.get(AppConstants.Refresh_Token);
            if (newRefreshToken) {
              this.serviceLocalStorage.setRefToken(newRefreshToken);
            }
            if (updateExpiresAt) {
              this.applySessionExpiryFromHeaders(response.headers);
            }
            return true;
          }

          return false;
        }),
        catchError(() => of(false))
      );
  }

  private getStoredSessionExpiryTimeMs(): number | null {
    const expiresAt = localStorage.getItem(AppConstants.SESSION_EXPIRES_AT_KEY);
    if (!expiresAt || isNaN(Number(expiresAt))) {
      return null;
    }
    return Number(expiresAt);
  }

  private showExpiryWarning(expiryTimeMs: number): void {
    if (this.expiryDialogOpen || !this.serviceLocalStorage.hasAuthToken()) {
      return;
    }

    this.expiryDialogOpen = true;
    this.expiryDialogRef = this.dialog.open(SessionExpiryDialogComponent, {
      width: '480px',
      disableClose: true,
      data: { expiryTimeMs }
    });

    this.expiryDialogRef.afterClosed().subscribe((result: string) => {
      this.expiryDialogOpen = false;
      this.expiryDialogRef = null;
      if (result === 'login') {
        this.logoutDueToExpiry(false);
      } else if (result === 'auto') {
        // auto-closed by logoutDueToExpiry timer — message already handled
      } else if (result === 'refresh') {
        this.continueSession();
      }
    });
  }

  private isLoginFlowError(message?: string): boolean {
    if (!message) {
      return false;
    }

    return (
      message === 'Authentication Failed: SSO-MFA Enabled' ||
      message === 'Authentication Failed: MFA Enabled' ||
      message === 'Authentication Failed: Bad credentials' ||
      message === 'Authentication Failed: Not able to find tenant DB' ||
      message === 'Account blocked!' ||
      message === 'User is disabled' ||
      message.includes('Authentication Failed: SSO Enabled')
    );
  }

  continueSession(): void {
    this.stopSessionMonitoring();
    this.continueSessionRefresh = true;
    this.tryRefreshToken(true).subscribe((success) => {
      if (!success) {
        this.continueSessionRefresh = false;
        this.logoutDueToExpiry();
      }
    });
  }
}
